
// =============================================================================
// FILE: src/api/healthcare/SupplementApi.ts
// =============================================================================

import { ENV } from '../../config/runtimeEnv';
import {
	SupplementRegimen,
	SupplementJobStatus,
	QueueJobPayload,
	CreateRegimenPayload,
	CreateItemPayload,
	ReorderPayload,
	SupplementRegimenItem,
} from '../../types/healthcare/SupplementTypes';
import type { UserUuid } from '../../types/identity';
import { http } from '../../lib/http/client';

const BASE = ENV.VITE_HEALTHCARE_SERVER;

// ── Regimen List ──────────────────────────────────────────────────────────────

export const listPatientRegimens = async (
	patientId: UserUuid
): Promise<SupplementRegimen[]> => {
	const { data } = await http.get(
		`${BASE}/practitioner/patients/${patientId}/supplement-regimens`
	);
	return data.regimens;
};

export const listPatientRegimensPatient = async (
	patientId: UserUuid
): Promise<SupplementRegimen[]> => {
	const { data } = await http.get(
		`${BASE}/patient/${patientId}/supplement-regimens`
	);
	return data.regimens;
};

// ── Single Regimen ────────────────────────────────────────────────────────────

export const getRegimen = async (uuid: string): Promise<SupplementRegimen> => {
	const { data } = await http.get(
		`${BASE}/practitioner/supplement-regimens/${uuid}`
	);
	return data.regimen;
};

export const createRegimen = async (
	payload: CreateRegimenPayload
): Promise<SupplementRegimen> => {
	const { data } = await http.post(
		`${BASE}/practitioner/supplement-regimens`,
		payload
	);
	return data.regimen;
};

export const updateRegimen = async (
	uuid: string,
	payload: { name?: string; notes?: string; summary?: string; status?: string }
): Promise<SupplementRegimen> => {
	const { data } = await http.put(
		`${BASE}/practitioner/supplement-regimens/${uuid}`,
		payload
	);
	return data.regimen;
};

export const archiveRegimen = async (uuid: string): Promise<void> => {
	await http.delete(`${BASE}/practitioner/supplement-regimens/${uuid}`);
};

export const duplicateRegimen = async (
	uuid: string
): Promise<SupplementRegimen> => {
	const { data } = await http.post(
		`${BASE}/practitioner/supplement-regimens/${uuid}/duplicate`,
		{}
	);
	return data.regimen;
};

// ── Regimen Items ─────────────────────────────────────────────────────────────

export const addRegimenItem = async (
	uuid: string,
	payload: CreateItemPayload
): Promise<SupplementRegimenItem> => {
	const { data } = await http.post(
		`${BASE}/practitioner/supplement-regimens/${uuid}/items`,
		payload
	);
	return data.item;
};

export const updateRegimenItem = async (
	uuid: string,
	itemId: number,
	payload: Partial<CreateItemPayload & { is_active: boolean; sort_order: number }>
): Promise<SupplementRegimenItem> => {
	const { data } = await http.put(
		`${BASE}/practitioner/supplement-regimens/${uuid}/items/${itemId}`,
		payload
	);
	return data.item;
};

export const removeRegimenItem = async (
	uuid: string,
	itemId: number
): Promise<void> => {
	await http.delete(
		`${BASE}/practitioner/supplement-regimens/${uuid}/items/${itemId}`
	);
};

export const reorderRegimenItems = async (
	uuid: string,
	items: ReorderPayload[]
): Promise<void> => {
	await http.post(
		`${BASE}/practitioner/supplement-regimens/${uuid}/items/reorder`,
		{ items }
	);
};

// ── AI Jobs ───────────────────────────────────────────────────────────────────

export const queueSupplementJob = async (
	payload: QueueJobPayload
): Promise<{ job_uuid: string; status: string }> => {
	const { data } = await http.post(
		`${BASE}/practitioner/supplement-jobs`,
		payload
	);
	return data;
};

export const getSupplementJobStatus = async (
	jobUuid: string
): Promise<SupplementJobStatus> => {
	const { data } = await http.get(
		`${BASE}/practitioner/supplement-jobs/${jobUuid}/status`
	);
	return data;
};

export const cancelSupplementJob = async (jobUuid: string): Promise<void> => {
	await http.post(
		`${BASE}/practitioner/supplement-jobs/${jobUuid}/cancel`,
		{}
	);
};

export const retrySupplementJob = async (jobUuid: string): Promise<void> => {
	await http.post(
		`${BASE}/practitioner/supplement-jobs/${jobUuid}/retry`,
		{}
	);
};